// Hideo - online saving in the Profiles window (logic without UI): the save line of each profile, the services shown with
// their state, the list of profile files found online. The services themselves are next to this file (services.ts,
// google.ts, dropbox.ts), the saving drive.ts, the autosave autosync.ts, images and voice notes media.ts; the dialogs are
// ProfilesDialog.vue, CloudPickDialog.vue and DriveFilesDialog.vue, their actions src/stores/cloud.ts.
import { t, tv } from '../../i18n'
import { type CloudMeta } from './common'

/** Last file saved or loaded for a profile on this device (SYNC[id].file, src/stores/sync.ts; kind and savedOn are stored values) */
interface SyncFile {
  /** ISO date written in the file */
  savedAt?: string
  /** 'telefono' | 'computer' (stored value) */
  savedOn?: string
  /** 'salvato' | 'caricato' | 'cartella' | 'online-salvato' | 'online-caricato' | old 'drive-salvato' / 'drive-caricato' */
  kind?: string
  /** Online service id ('google', 'dropbox') */
  provider?: string
}
/** Save state of a profile on this device (syncOf, src/stores/sync.ts) */
export interface SyncState {
  /** Changes not saved in a file or online yet */
  dirty?: boolean
  file?: SyncFile
}

/** The save line under a profile's name: tone (colour and icon) and text */
interface SyncLine {
  tone: 'dirty' | 'never' | 'ok'
  text: string
}
/** Colour and icon of each tone (classes written whole) */
export const SYNC_LINE_LOOK: Record<SyncLine['tone'], { cls: string; icon: string }> = {
  dirty: { cls: 'text-brand font-semibold', icon: 'fa-circle-exclamation' },
  never: { cls: 'text-gray-500', icon: 'fa-file' },
  ok: { cls: 'text-green-400', icon: 'fa-check' }
}

/** The profile's save line. service = name of the file's online service (Google Drive for the old "drive-…" kinds and
 *  for a file without provider); fmtWhen = date and time in the UI language ('' when not valid) */
export function syncLine(s: SyncState, service: string, fmtWhen: (iso: string) => string): SyncLine {
  const f = s.file
  if (s.dirty) {
    return { tone: 'dirty', text: f && f.savedAt ? t('Changes not saved in a file (last file {when})', { when: fmtWhen(f.savedAt) }) : t('Changes not saved in a file') }
  }
  if (!f) return { tone: 'never', text: t('Never saved in a file') }
  const status = ({
    salvato: t('Up to date: file saved'), caricato: t('Up to date: file loaded'), cartella: t('Up to date: saved in the folder'),
    'drive-salvato': t('Up to date: saved on {service}', { service: 'Google Drive' }), 'drive-caricato': t('Up to date: loaded from {service}', { service: 'Google Drive' }),
    'online-salvato': t('Up to date: saved on {service}', { service }), 'online-caricato': t('Up to date: loaded from {service}', { service })
  } as Record<string, string>)[f.kind || ''] || t('Up to date with the file')
  const when = f.savedAt ? fmtWhen(f.savedAt) : '', device = f.savedOn ? tv(f.savedOn) : ''
  const text = when && device ? t('{status} · {when} from the {device}', { status, when, device })
    : when ? `${status} · ${when}` : device ? t('{status} from the {device}', { status, device }) : status
  return { tone: 'ok', text }
}

/** An online service as the dialogs show it (CLOUDS[id], services.ts, and its state on this device) */
export interface CloudInfo {
  id: string
  name: string
  /** Font Awesome classes of its logo */
  icon: string
  /** Hideo can use it (its app key is set) */
  configured: boolean
  /** Connected on this device */
  on: boolean
  /** Account email ('' when unknown) */
  email: string
}
/** "Connected" or "Connected as <email>" */
export const connectedAs = (c: Pick<CloudInfo, 'email'>): string => (c.email ? t('Connected as {email}', { email: c.email }) : t('Connected'))
/** State line of a service in the online-saving section of the Profiles window */
export const cloudState = (c: CloudInfo): string => (!c.configured ? t('Coming soon') : c.on ? connectedAs(c) : t('Not connected'))
/** State line of a service in the "Save online" / "Load online" picker (linked = the profile is already saved there) */
export const cloudPickState = (c: CloudInfo, linked: boolean): string =>
  !c.configured ? t('Coming soon') : c.on ? (linked ? t('Connected · already used for this profile') : connectedAs(c)) : t('Not connected: you will be asked to sign in')

/** "A or B" in the UI language */
export function orList(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { type: 'disjunction' }).format(items)
  } catch {
    return items.join(' / ')
  }
}

/** A profile file on a service, as the lists show it: the services' normalised "meta" (CloudMeta, services.ts) */
export type CloudFile = CloudMeta
/** Name shown for a file: without the stored prefix (current or former app name) and without ".json" */
export const driveFileLabel = (f: Pick<CloudFile, 'name'>): string =>
  (f.name || '').replace(/^(Hideo|Ideo|Flagenda|PedlyFlag|Pedly|CreatorOS) - /, '').replace(/\.json$/i, '')
/** The files in the service's order (newest first), with the one linked to the profile at the top */
export const sortFiles = <T extends Pick<CloudFile, 'id'>>(files: T[], linked: string | undefined): T[] =>
  [...files].sort((a, b) => Number(b.id === linked) - Number(a.id === linked))
/** "Saved on <when> (from the phone / computer)" line of a file */
export function fileSavedLine(f: CloudFile, fmtWhen: (iso: string) => string): string {
  const when = fmtWhen(f.savedAt || f.modified || ''), on = f.savedOn === 'telefono' || f.savedOn === 'computer' ? f.savedOn : ''
  return on ? t('Saved on {when} from the {device}', { when, device: tv(on) }) : t('Saved on {when}', { when })
}
